import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { money } from "@/components/opportunities/shared";
import { formatDate } from "@/lib/dates";
import { CircleNotch, Plus } from "@phosphor-icons/react";

function Fld({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">{label}{required ? " *" : ""}</Label>
      {children}
    </div>
  );
}

async function loadFinance(setState) {
  try {
    const [goals, collections, commissions, bonuses, users, opps] = await Promise.all([
      api.get("/sales-goals"), api.get("/collections"), api.get("/commissions"),
      api.get("/achievement-bonuses"), api.get("/users/options"),
      api.get("/opportunities", { params: { archived: "all" } }),
    ]);
    setState({
      goals: goals.data, collections: collections.data, commissions: commissions.data,
      bonuses: bonuses.data, users: users.data,
      wonOpps: opps.data.filter((o) => o.outcome === "won"),
    });
  } catch (e) {
    toast.error("Finance data could not be loaded. Please refresh.");
  }
}

async function act(fn, onDone) {
  try {
    await fn();
    onDone();
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail));
  }
}

const STATUS_COLORS = {
  projected: "bg-[#F4F4F5] text-[#52525B]", approved: "bg-[#3B82F6]/10 text-[#1D4ED8]",
  earned: "bg-[#569A1F]/15 text-[#457d18]", paid: "bg-[#569A1F] text-white",
  voided: "bg-[#EF4444]/10 text-[#B91C1C]", pending_approval: "bg-[#F59E0B]/10 text-[#B45309]",
};
function StatusChip({ status }) {
  return <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase ${STATUS_COLORS[status] || "bg-[#F4F4F5]"}`}>{status.replace("_", " ")}</span>;
}

function GoalDialog({ goal, users, onClose, onDone }) {
  const [form, setForm] = useState({
    salesperson_id: goal?.salesperson_id || "", goal_period_start: goal?.goal_period_start || "",
    goal_period_end: goal?.goal_period_end || "", target_credited_revenue: goal?.target_credited_revenue ?? "",
    label: goal?.label || "",
  });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const submit = (e) => {
    e.preventDefault();
    const body = { ...form, target_credited_revenue: Number(form.target_credited_revenue) };
    act(async () => {
      if (goal) await api.patch(`/goals/${goal.id}`, body);
      else await api.post("/goals", body);
      toast.success(goal ? "Goal updated" : "Goal created");
      onClose();
    }, onDone);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="goal-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">{goal ? "Edit Goal" : "New Goal"}</DialogTitle>
          <DialogDescription>Targets are credited First 12-Month Contract Revenue. Periods max 12 months.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {!goal && (
            <Fld label="Salesperson" required>
              <Select value={form.salesperson_id} onValueChange={set("salesperson_id")} required>
                <SelectTrigger className="rounded-sm" data-testid="goal-salesperson"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Period start" required><DatePicker value={form.goal_period_start} onChange={set("goal_period_start")} required testId="goal-start" /></Fld>
            <Fld label="Period end" required><DatePicker value={form.goal_period_end} onChange={set("goal_period_end")} required testId="goal-end" /></Fld>
          </div>
          <Fld label="Target credited revenue $" required>
            <Input type="number" min="1" required value={form.target_credited_revenue} onChange={set("target_credited_revenue")} className="rounded-sm" data-testid="goal-target-input" />
          </Fld>
          <Fld label="Label"><Input value={form.label} onChange={set("label")} className="rounded-sm" data-testid="goal-label" /></Fld>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="submit-goal">Save goal</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BillingDialog({ wonOpps, onClose, onDone }) {
  const [form, setForm] = useState({
    opportunity_id: "", service_period: "", billed_amount: "",
    sale_classification: "original_sale", renewal_id: "", note: "",
  });
  const [renewals, setRenewals] = useState([]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const pickOpp = async (id) => {
    set("opportunity_id")(id);
    try {
      const { data } = await api.get(`/opportunities/${id}/renewals`);
      setRenewals(data);
    } catch (e) { setRenewals([]); }
  };
  const submit = (e) => {
    e.preventDefault();
    act(async () => {
      const { data } = await api.post("/billings", {
        ...form, billed_amount: Number(form.billed_amount),
        renewal_id: form.renewal_id || null, note: form.note || null,
      });
      if (data.commission) {
        toast.success(`Billing recorded — ${money(data.commission.calculated_amount)} commission calculated on the full ${money(data.billed_amount)} billing. It is HELD until collected in full, or the owner approves an early release.`, { duration: 8000 });
      } else if (data.commission_skipped_reason) toast.warning(data.commission_skipped_reason, { duration: 8000 });
      else toast.success("Billing recorded");
      onClose();
    }, onDone);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-md" data-testid="billing-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">Record Gross Billing</DialogTitle>
          <DialogDescription>States the actual billed amount for a service month with nothing collected yet — no collection entry is created. The commission is calculated on the full billing and held until collected or released by the owner.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Fld label="Opportunity (won)" required>
            <Select value={form.opportunity_id} onValueChange={pickOpp} required>
              <SelectTrigger className="rounded-sm" data-testid="billing-opportunity"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{wonOpps.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} — {o.prospect_name}</SelectItem>)}</SelectContent>
            </Select>
          </Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Service period (YYYY-MM)" required><Input required placeholder="2026-09" pattern="\d{4}-\d{2}" value={form.service_period} onChange={set("service_period")} className="rounded-sm" data-testid="billing-period" /></Fld>
            <Fld label="Gross billed $" required><Input type="number" min="0.01" step="0.01" required value={form.billed_amount} onChange={set("billed_amount")} className="rounded-sm" data-testid="billing-amount" /></Fld>
          </div>
          <Fld label="Classification" required>
            <Select value={form.sale_classification} onValueChange={set("sale_classification")}>
              <SelectTrigger className="rounded-sm" data-testid="billing-classification"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="original_sale">Original sale</SelectItem>
                <SelectItem value="tag_work">Tag work</SelectItem>
                <SelectItem value="renewal">Renewal</SelectItem>
              </SelectContent>
            </Select>
          </Fld>
          {form.sale_classification === "renewal" && (
            <Fld label="Renewal" required>
              <Select value={form.renewal_id} onValueChange={set("renewal_id")} required>
                <SelectTrigger className="rounded-sm" data-testid="billing-renewal"><SelectValue placeholder="Select renewal" /></SelectTrigger>
                <SelectContent>{renewals.map((r) => <SelectItem key={r.id} value={r.id}>{r.renewal_period_start} → {r.renewal_period_end}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
          )}
          <Fld label="Note"><Input value={form.note} onChange={set("note")} className="rounded-sm" data-testid="billing-note" /></Fld>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="submit-billing">Record billing</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CollectionDialog({ wonOpps, onClose, onDone }) {
  const [form, setForm] = useState({
    opportunity_id: "", service_period: "", collection_date: "", amount: "", billed_amount: "",
    revenue_type: "recurring", sale_classification: "original_sale", renewal_id: "", reference: "", note: "",
  });
  const [renewals, setRenewals] = useState([]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const pickOpp = async (id) => {
    set("opportunity_id")(id);
    try {
      const { data } = await api.get(`/opportunities/${id}/renewals`);
      setRenewals(data);
    } catch (e) { setRenewals([]); }
  };
  const submit = (e) => {
    e.preventDefault();
    act(async () => {
      const { data } = await api.post("/collections", {
        ...form, amount: Number(form.amount),
        billed_amount: form.billed_amount === "" ? null : Number(form.billed_amount),
        renewal_id: form.renewal_id || null, note: form.note || null,
      });
      if (data.commission) toast.success(`Collection recorded — commission calculated on the full ${money(data.billing?.billed_amount)} billing`);
      else if (data.commission_skipped_reason) toast.warning(data.commission_skipped_reason, { duration: 8000 });
      else toast.success("Collection recorded");
      if (data.billing && !data.billing.fully_collected) {
        toast.info(`Partial collection: ${money(data.billing.collected_total)} of ${money(data.billing.billed_amount)} for ${data.billing.service_period} — commission payment held until fully collected (owner may approve an early release).`, { duration: 8000 });
      }
      onClose();
    }, onDone);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-md" data-testid="collection-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">Record Collected Revenue</DialogTitle>
          <DialogDescription>Commissions become eligible only after revenue is collected.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Fld label="Opportunity (won)" required>
            <Select value={form.opportunity_id} onValueChange={pickOpp} required>
              <SelectTrigger className="rounded-sm" data-testid="collection-opportunity"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{wonOpps.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} — {o.prospect_name}</SelectItem>)}</SelectContent>
            </Select>
          </Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Service period (YYYY-MM)" required><Input required placeholder="2026-08" pattern="\d{4}-\d{2}" value={form.service_period} onChange={set("service_period")} className="rounded-sm" data-testid="collection-period" /></Fld>
            <Fld label="Collection date" required><DatePicker value={form.collection_date} onChange={set("collection_date")} required testId="collection-date" /></Fld>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Amount collected $" required><Input type="number" min="0.01" step="0.01" required value={form.amount} onChange={set("amount")} className="rounded-sm" data-testid="collection-amount" /></Fld>
            <Fld label="Revenue type" required>
              <Select value={form.revenue_type} onValueChange={set("revenue_type")}>
                <SelectTrigger className="rounded-sm" data-testid="collection-revenue-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="recurring">Recurring</SelectItem>
                  <SelectItem value="tag_work">Tag work</SelectItem>
                </SelectContent>
              </Select>
            </Fld>
          </div>
          <Fld label="Gross billed for this service month $">
            <Input type="number" min="0.01" step="0.01" value={form.billed_amount} onChange={set("billed_amount")} className="rounded-sm" data-testid="collection-billed-amount" />
            <p className="text-[11px] text-[#6D6D70]">Required on the first collection of a service month — commission is calculated on this full billing, never on a partial receipt. Leave blank on later partial payments for the same month.</p>
          </Fld>
          <Fld label="Classification" required>
            <Select value={form.sale_classification} onValueChange={set("sale_classification")}>
              <SelectTrigger className="rounded-sm" data-testid="collection-classification"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="original_sale">Original sale</SelectItem>
                <SelectItem value="tag_work">Tag work</SelectItem>
                <SelectItem value="renewal">Renewal</SelectItem>
              </SelectContent>
            </Select>
          </Fld>
          {form.sale_classification === "renewal" && (
            <Fld label="Renewal period" required>
              <Select value={form.renewal_id} onValueChange={set("renewal_id")} required>
                <SelectTrigger className="rounded-sm" data-testid="collection-renewal"><SelectValue placeholder="Select renewal" /></SelectTrigger>
                <SelectContent>{renewals.map((r) => <SelectItem key={r.id} value={r.id}>Starts {formatDate(r.renewal_start_date)}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
          )}
          <Fld label="Unique reference" required><Input required value={form.reference} onChange={set("reference")} className="rounded-sm" placeholder="e.g. DEP-2026-0813-001" data-testid="collection-reference" /></Fld>
          <Fld label="Note"><Input value={form.note} onChange={set("note")} className="rounded-sm" data-testid="collection-note" /></Fld>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="submit-collection">Record collection</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RenewalDialog({ wonOpps, onClose, onDone }) {
  const [form, setForm] = useState({ opportunity_id: "", renewal_start_date: "", term_months: 12, note: "" });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const submit = (e) => {
    e.preventDefault();
    act(async () => {
      await api.post(`/opportunities/${form.opportunity_id}/renewals`, {
        renewal_start_date: form.renewal_start_date, term_months: Number(form.term_months), note: form.note || null,
      });
      toast.success("Renewal period created");
      onClose();
    }, onDone);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="renewal-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">New Renewal Period</DialogTitle>
          <DialogDescription>A renewal begins a new 12-month interval linked to the original account.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Fld label="Opportunity (won)" required>
            <Select value={form.opportunity_id} onValueChange={set("opportunity_id")} required>
              <SelectTrigger className="rounded-sm" data-testid="renewal-opportunity"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{wonOpps.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} — {o.prospect_name}</SelectItem>)}</SelectContent>
            </Select>
          </Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Renewal start" required><DatePicker value={form.renewal_start_date} onChange={set("renewal_start_date")} required testId="renewal-start" /></Fld>
            <Fld label="Term (months)" required><Input type="number" min="1" max="60" required value={form.term_months} onChange={set("term_months")} className="rounded-sm" data-testid="renewal-term" /></Fld>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="submit-renewal">Create renewal</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const ORIGIN_LABELS = { shirley_originated: "Self-Originated", company_assigned: "Company-Assigned", tag_work: "Tag Work" };

export default function Finance() {
  const { isAdmin, user } = useAuth();
  const isGlobalAdmin = user?.role === "global_administrator";
  const [state, setState] = useState(null);
  const [tab, setTab] = useState("commissions");
  const [dialog, setDialog] = useState(null); // {type: goal|collection|renewal, goal?}
  const helpers = useRef({ loadFinance });
  const load = useCallback(() => helpers.current.loadFinance(setState), [helpers, setState]);
  useEffect(() => { load(); }, [load]);

  if (!state) return <div className="flex justify-center py-24"><CircleNotch size={26} className="animate-spin text-[#032852]" /></div>;

  const commissionAction = (id, action, extra) =>
    act(async () => {
      await api.post(`/commissions/${id}/${action}`, extra);
      toast.success(`Commission ${action === "pay" ? "marked paid" : action + "d"}`);
    }, load);
  const bonusAction = (id, action) =>
    act(async () => {
      await api.post(`/achievement-bonuses/${id}/${action}`);
      toast.success(`Bonus ${action === "pay" ? "marked paid" : "approved"}`);
    }, load);

  return (
    <div className="p-6 sm:p-8" data-testid="finance-page">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#569A1F]">Revenue & Compensation</p>
      <h1 className="mt-2 font-heading text-3xl font-black tracking-tight text-[#032852] sm:text-4xl">Finance</h1>
      <p className="mt-2 text-sm text-[#6D6D70]">{isAdmin ? "Goals, collected revenue and the commission workflow." : "Your goals, collections and commissions — read-only."}</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="gap-1 rounded-sm border border-[#E4E4E7] bg-[#F4F4F5] p-1">
          {["commissions", "collections", "goals", "bonuses"].map((t) => (
            <TabsTrigger key={t} value={t}
              className="rounded-sm border border-transparent px-4 text-xs font-bold uppercase tracking-wide data-[state=active]:border-[#E4E4E7] data-[state=active]:bg-white data-[state=active]:text-[#032852] data-[state=active]:shadow-sm"
              data-testid={`tab-${t}`}>{t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="commissions" className="mt-4">
          <div className="overflow-x-auto rounded-sm border border-[#E4E4E7] bg-white" data-testid="commissions-table">
            <table className="w-full text-left text-sm">
              <thead><tr className="border-b border-[#E4E4E7] text-[10px] font-bold uppercase text-[#6D6D70]">
                {["Opportunity", "Type", "Month", "Rate", "Basis", "Amount", "Status", isAdmin ? "Actions" : ""].map((hd) => <th key={hd} className="px-3 py-2.5">{hd}</th>)}
              </tr></thead>
              <tbody>
                {state.commissions.map((cm) => (
                  <tr key={cm.id} className="border-b border-[#F4F4F5] last:border-0" data-testid={`commission-row-${cm.id}`}>
                    <td className="max-w-[220px] truncate px-3 py-2 font-semibold text-[#09090B]">{cm.opportunity_name}</td>
                    <td className="px-3 py-2 text-[#52525B]">{cm.commission_type}{cm.accelerated ? " ⚡" : ""}{cm.minimum_applied ? " (min $300)" : ""}</td>
                    <td className="px-3 py-2 text-[#52525B]">{cm.month_index ?? "—"}</td>
                    <td className="px-3 py-2 text-[#52525B]">{(cm.rate_applied * 100).toFixed(1)}%</td>
                    <td className="px-3 py-2 text-[#52525B]">{money(cm.basis_amount)}</td>
                    <td className="px-3 py-2 font-semibold text-[#032852]" data-testid={`commission-amount-${cm.id}`}>{money(cm.calculated_amount)}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col items-start gap-1">
                        <StatusChip status={cm.status} />
                        {cm.payment_eligible === false && (
                          <span className="whitespace-nowrap rounded-sm bg-[#F59E0B]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#B45309]" data-testid={`held-chip-${cm.id}`}>
                            Held · {money(cm.billing?.collected_total)} / {money(cm.billing?.billed_amount)}
                          </span>
                        )}
                        {cm.early_release && (
                          <span className="whitespace-nowrap rounded-sm bg-[#2E86D6]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#1D4ED8]" data-testid={`released-chip-${cm.id}`}>
                            Early release approved
                          </span>
                        )}
                      </div>
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1.5">
                          {cm.status === "projected" && (
                            <Button size="sm" className="h-7 rounded-sm bg-[#569A1F] px-2.5 text-xs font-bold text-white hover:bg-[#457d18]"
                              onClick={() => commissionAction(cm.id, "approve")} data-testid={`approve-${cm.id}`}>
                              Approve
                            </Button>
                          )}
                          {cm.status === "approved" && (
                            <Button size="sm" className="h-7 rounded-sm bg-[#569A1F] px-2.5 text-xs font-bold text-white hover:bg-[#457d18]"
                              onClick={() => commissionAction(cm.id, "earn")} data-testid={`earn-${cm.id}`}>
                              Mark earned
                            </Button>
                          )}
                          {cm.status === "earned" && (
                            <Button size="sm" className="h-7 rounded-sm bg-[#569A1F] px-2.5 text-xs font-bold text-white hover:bg-[#457d18]"
                              onClick={() => commissionAction(cm.id, "pay")} data-testid={`pay-${cm.id}`}>
                              Mark paid
                            </Button>
                          )}
                          {isGlobalAdmin && cm.payment_eligible === false && !["paid", "voided"].includes(cm.status) && (
                            <Button size="sm" variant="outline" className="h-7 rounded-sm border-[#2E86D6]/50 px-2.5 text-xs font-bold text-[#1D4ED8] hover:bg-[#2E86D6]/5"
                              onClick={() => { const reason = window.prompt("Early release reason (owner authority — makes the commission payable, does NOT pay it):"); if (reason) commissionAction(cm.id, "release", { reason }); }} data-testid={`release-${cm.id}`}>
                              Release
                            </Button>
                          )}
                          {["projected", "approved", "earned"].includes(cm.status) && (
                            <Button size="sm" variant="outline" className="h-7 rounded-sm border-[#EF4444]/50 px-2.5 text-xs font-bold text-[#B91C1C] hover:bg-[#EF4444]/5"
                              onClick={() => { const reason = window.prompt("Void reason:"); if (reason) commissionAction(cm.id, "void", { reason }); }} data-testid={`void-${cm.id}`}>
                              Void
                            </Button>
                          )}
                          {cm.status === "paid" && !cm.adjustment_of && (
                            <Button size="sm" variant="outline" className="h-7 rounded-sm border-[#B45309]/50 px-2.5 text-xs font-bold text-[#B45309] hover:bg-[#F59E0B]/10"
                              onClick={() => { const delta = window.prompt("Adjustment amount (+/-):"); const reason = delta && window.prompt("Reason:"); if (delta && reason) commissionAction(cm.id, "adjust", { delta_amount: Number(delta), reason }); }} data-testid={`adjust-${cm.id}`}>
                              Adjust
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {state.commissions.length === 0 && (
                  <tr>
                    <td colSpan="8" className="px-3 py-10 text-center" data-testid="commissions-empty-state">
                      <p className="text-sm font-semibold text-[#032852]">No commissions yet — commissions generate only when collected revenue is recorded.</p>
                      <p className="mx-auto mt-1.5 max-w-md text-xs text-[#6D6D70]">
                        Record a collected service month against a won opportunity and the commission is
                        calculated automatically under rule P6-2026.06 (Fully Developed 10% + 1%, Assisted 7% + 1%,
                        Tag Work 10% one-time, Renewal 5% one-time).
                      </p>
                      {isAdmin ? (
                        <Button size="sm" className="mt-3 rounded-sm bg-[#569A1F] text-xs font-bold text-white hover:bg-[#457d18]"
                          onClick={() => setTab("collections")} data-testid="go-to-collections-btn">
                          Go to Collections to record one
                        </Button>
                      ) : (
                        <p className="mt-2 text-xs text-[#6D6D70]">An administrator records collected revenue under the Collections tab.</p>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-[#9CA3AF]">Commission rules version P6-2026.06 (June 8, 2026 Sales &amp; Marketing Operations Manual). Historical records retain the rule version in force when they were calculated.</p>
        </TabsContent>

        <TabsContent value="collections" className="mt-4">
          {isAdmin && (
            <div className="mb-3 flex justify-end gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setDialog({ type: "renewal" })} data-testid="new-renewal-btn">New renewal</Button>
              <Button variant="outline" className="rounded-sm border-[#569A1F]/50 font-semibold text-[#3d6e15] hover:bg-[#569A1F]/5" onClick={() => setDialog({ type: "billing" })} data-testid="record-billing-btn">Record billing</Button>
              <Button className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" onClick={() => setDialog({ type: "collection" })} data-testid="record-collection-btn">
                <Plus size={15} weight="bold" /> Record collection
              </Button>
            </div>
          )}
          <div className="overflow-x-auto rounded-sm border border-[#E4E4E7] bg-white" data-testid="collections-table">
            <table className="w-full text-left text-sm">
              <thead><tr className="border-b border-[#E4E4E7] text-[10px] font-bold uppercase text-[#6D6D70]">
                {["Period", "Date", "Amount", "Billed (month)", "Type", "Classification", "Month #", "Reference"].map((hd) => <th key={hd} className="px-3 py-2.5">{hd}</th>)}
              </tr></thead>
              <tbody>
                {state.collections.map((col) => (
                  <tr key={col.id} className="border-b border-[#F4F4F5] last:border-0" data-testid={`collection-row-${col.id}`}>
                    <td className="px-3 py-2 font-semibold text-[#09090B]">{col.service_period}</td>
                    <td className="px-3 py-2 text-[#52525B]">{formatDate(col.collection_date)}</td>
                    <td className="px-3 py-2 font-semibold text-[#032852]">{money(col.amount)}</td>
                    <td className="px-3 py-2 text-[#52525B]">
                      {col.billed_amount != null ? money(col.billed_amount) : "—"}
                      {col.billing_fully_collected === false && <span className="ml-1.5 rounded-sm bg-[#F59E0B]/10 px-1 py-0.5 text-[9px] font-bold uppercase text-[#B45309]">Partial</span>}
                    </td>
                    <td className="px-3 py-2 text-[#52525B]">{col.revenue_type}</td>
                    <td className="px-3 py-2 text-[#52525B]">{col.sale_classification}</td>
                    <td className="px-3 py-2 text-[#52525B]">{col.month_index}</td>
                    <td className="px-3 py-2 text-xs text-[#6D6D70]">{col.reference}</td>
                  </tr>
                ))}
                {state.collections.length === 0 && <tr><td colSpan="8" className="px-3 py-8 text-center text-sm text-[#6D6D70]">No collections recorded.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="goals" className="mt-4">
          {isAdmin && (
            <div className="mb-3 flex justify-end">
              <Button className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" onClick={() => setDialog({ type: "goal" })} data-testid="new-goal-btn">
                <Plus size={15} weight="bold" /> New goal
              </Button>
            </div>
          )}
          <div className="space-y-2" data-testid="goals-list">
            {state.goals.filter((g) => g.target_credited_revenue != null).map((g) => (
              <div key={g.id} className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-[#E4E4E7] bg-white p-4" data-testid={`goal-row-${g.id}`}>
                <div>
                  <p className="text-sm font-semibold text-[#09090B]">{g.label || "Goal"} <span className="text-xs text-[#6D6D70]">({formatDate(g.goal_period_start)} → {formatDate(g.goal_period_end)})</span></p>
                  <p className="text-xs text-[#6D6D70]">{(state.users.find((u) => u.id === g.salesperson_id) || {}).name}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-heading text-lg font-black text-[#032852]">{money(g.target_credited_revenue)}</span>
                  {isAdmin && <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => setDialog({ type: "goal", goal: g })} data-testid={`edit-goal-${g.id}`}>Edit</Button>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="bonuses" className="mt-4">
          <div className="space-y-2" data-testid="bonuses-list">
            {state.bonuses.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-[#E4E4E7] bg-white p-4" data-testid={`bonus-row-${b.id}`}>
                <div>
                  <p className="text-sm font-semibold text-[#09090B]">{money(b.threshold)} threshold — {money(b.amount)} bonus</p>
                  <p className="text-xs text-[#6D6D70]">Achieved {formatDate(b.achieved_date)} · credited {money(b.supporting_credited_revenue)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusChip status={b.status} />
                  {isAdmin && b.status === "pending_approval" && <Button size="sm" className="rounded-sm bg-[#032852] text-xs text-white" onClick={() => bonusAction(b.id, "approve")} data-testid={`approve-bonus-${b.id}`}>Approve</Button>}
                  {isAdmin && b.status === "approved" && <Button size="sm" className="rounded-sm bg-[#569A1F] text-xs text-white" onClick={() => bonusAction(b.id, "pay")} data-testid={`pay-bonus-${b.id}`}>Mark paid</Button>}
                </div>
              </div>
            ))}
            {state.bonuses.length === 0 && <p className="rounded-sm border border-dashed border-[#E4E4E7] bg-white py-10 text-center text-sm text-[#6D6D70]">No achievement bonuses yet.</p>}
          </div>
        </TabsContent>
      </Tabs>

      {dialog?.type === "goal" && <GoalDialog goal={dialog.goal} users={state.users} onClose={() => setDialog(null)} onDone={load} />}
      {dialog?.type === "collection" && <CollectionDialog wonOpps={state.wonOpps} onClose={() => setDialog(null)} onDone={load} />}
      {dialog?.type === "billing" && <BillingDialog wonOpps={state.wonOpps} onClose={() => setDialog(null)} onDone={load} />}
      {dialog?.type === "renewal" && <RenewalDialog wonOpps={state.wonOpps} onClose={() => setDialog(null)} onDone={load} />}
    </div>
  );
}

export { ORIGIN_LABELS };
